import React from 'react';

import { SortableTable, Text } from '~/components/core';
import CounterWithRefresh from '~/components/core/CounterWithRefresh/CounterWithRefresh';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import type { PaginationProps } from '~/components/core/Tables/SortableTable/TablePaginationSection/types';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import type { FilterParams, GeneratedReport, SortableColumn } from './hooks/useGeneratedReports';
import ReportStatusBadge from './ReportStatusBadge';
import { GENERATED_REPORTS_TEXTS } from './texts';

interface GeneratedReportsTableProps {
  reports: GeneratedReport[];
  total: number;
  loading: boolean;
  isError?: boolean;
  filterParams: FilterParams;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onDownload: (reportId: number) => void;
  onDelete: (reportId: number) => Promise<boolean>;
  onSortByColumn?: (columnId: string, direction: 'asc' | 'desc') => void;
  sortBy: SortableColumn;
  orderBy: 'asc' | 'desc';
  onRefresh?: () => void;
}

const GeneratedReportsTable: React.FC<GeneratedReportsTableProps> = ({
  reports,
  total,
  loading,
  isError,
  filterParams,
  onPageChange,
  onLimitChange,
  onDownload,
  onDelete,
  onSortByColumn,
  sortBy,
  orderBy,
  onRefresh,
}) => {
  const tableColumns = [
    {
      id: 'name',
      label: GENERATED_REPORTS_TEXTS.COLUMN_NAME,
      disableSort: true,
      specialCell: (report: GeneratedReport) => (
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
          {report.name || report.report_template_name || '-'}
        </Text>
      ),
    },
    {
      id: 'requested_at',
      label: GENERATED_REPORTS_TEXTS.COLUMN_DATE_CREATED,
      specialCell: (report: GeneratedReport) => (
        <Text variant={Text.VARIANTS.SM}>{report.requested_at ? serverDateTimeToLocal(report.requested_at) : '-'}</Text>
      ),
    },
    {
      id: 'requested_by_name',
      label: GENERATED_REPORTS_TEXTS.COLUMN_CREATED_BY,
      disableSort: true,
      specialCell: (report: GeneratedReport) => (
        <Text variant={Text.VARIANTS.SM}>{report.requested_by_name || '-'}</Text>
      ),
    },
    {
      id: 'status',
      label: GENERATED_REPORTS_TEXTS.COLUMN_STATUS,
      specialCell: (report: GeneratedReport) => (
        <ReportStatusBadge status={report.status} errorMessage={report.error_message} />
      ),
    },
    {
      id: 'actions',
      label: GENERATED_REPORTS_TEXTS.COLUMN_ACTIONS,
      disableSort: true,
      width: '80px',
      specialCell: (report: GeneratedReport) => {
        const isDownloadable = report.status === 'completed';
        return (
          <ThreeDotsMenu
            options={[
              {
                key: `download-${report.id}`,
                node: GENERATED_REPORTS_TEXTS.ACTION_DOWNLOAD,
                onClick: () => onDownload(report.id),
                disabled: !isDownloadable,
                tooltipText: !isDownloadable ? GENERATED_REPORTS_TEXTS.DOWNLOAD_NOT_READY_TOOLTIP : undefined,
              },
              {
                key: `delete-${report.id}`,
                node: GENERATED_REPORTS_TEXTS.ACTION_DELETE,
                onClick: () => onDelete(report.id),
                withConfirmProps: {
                  title: GENERATED_REPORTS_TEXTS.DELETE_DIALOG_TITLE,
                  contentText: GENERATED_REPORTS_TEXTS.DELETE_DIALOG_CONTENT,
                  primaryButtonName: GENERATED_REPORTS_TEXTS.DELETE_DIALOG_CONFIRM,
                  shouldCloseOnPrimary: true,
                },
              },
            ]}
          />
        );
      },
    },
  ];

  const paginationProps: PaginationProps = {
    page: filterParams.page - 1,
    count: total,
    rowsPerPage: filterParams.limit,
    onChangePage: (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
      onPageChange(page + 1);
    },
    onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => {
      onLimitChange(parseInt(event.target.value, 10));
    },
    rowsPerPageOptions: [10, 20, 50, 100],
  };

  const handleSortByColumn = (_event: React.MouseEvent, { id, order }: { id: string; order: 'asc' | 'desc' }) => {
    if (id === 'requested_at' || id === 'status') {
      onSortByColumn?.(id, order);
    }
  };

  const hasActiveFilters =
    filterParams.name !== '' ||
    filterParams.status.length > 0 ||
    filterParams.requestedAtStart !== '' ||
    filterParams.requestedAtEnd !== '';

  if (loading || isError) {
    return <SkeletonTable rowsCount={5} columnsCount={5} isError={isError} />;
  }

  return (
    <SortableTable
      rows={reports}
      columns={tableColumns}
      stickyHeader
      paginationProps={paginationProps}
      onSortByColumn={handleSortByColumn}
      order={orderBy}
      defaultOrderColumn={tableColumns.findIndex((column) => column.id === sortBy)}
      counterViewText={
        <CounterWithRefresh
          onRefresh={onRefresh}
          isLoading={loading}
          pagination={{ totalCount: total, resultsTruncated: false }}
          refreshTooltipText="Refresh reports"
          refreshAriaLabel="Refresh reports"
        />
      }
      emptyStateComponent={
        <div className="py-48 flex flex-col items-center justify-center">
          <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.MEDIUM} className="mb-8 text-slate-600">
            {GENERATED_REPORTS_TEXTS.EMPTY_STATE_TITLE}
          </Text>
          <Text variant={Text.VARIANTS.SM} className="text-slate-500">
            {hasActiveFilters
              ? GENERATED_REPORTS_TEXTS.EMPTY_STATE_ADJUST_FILTERS
              : GENERATED_REPORTS_TEXTS.EMPTY_STATE_FIRST_REPORT}
          </Text>
        </div>
      }
    />
  );
};

export default GeneratedReportsTable;
